import Konva from "konva";
import {
  addImageToStage,
  loadImage,
} from "@/components/mobile/generation/util";
import { Ref, ref } from "vue";
import { TraitAnimationType } from "@/enums";

interface Options {
  duration?: number; // 每帧持续时间（恒定）
  durationGetter?: (index: number) => number; // 每帧持续时间（随机）
  draggable?: boolean;
  nodeProps?: {
    name?: string;
    id?: string;
    mateTraitAnimationId?: number;
    transform?: MediaTransform;
  };
}

function batchLoadImages(srcList: string[]): Promise<HTMLImageElement[]> {
  return Promise.all(srcList.map(loadImage));
}

/**
 * 使用帧动画，动画整体放在一个group中
 * @param stage 舞台
 * @param layer 图层
 * @param keyFrameUrls 每一帧图片的url
 * @param options 播放选项
 * @param options.duration 每帧持续时间（恒定）
 * @param options.durationGetter 每帧持续时间（随机）
 * @param options.draggable 是否可拖拽
 */
export const usePerformAnimationWithCanvas = (
  stage: Ref<Konva.Stage | null>,
  layer: Ref<Konva.Layer | Konva.Group | null>,
  keyFrameUrls: Ref<string[]>,
  options: Options = {
    duration: 1000,
    draggable: false,
    nodeProps: {},
  }
) => {
  const frames = ref<HTMLImageElement[]>([]);
  let interval: number = 0;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  const animationShape = ref<Konva.Image | null>(null);

  const prepareFrames = async () => {
    frames.value = await batchLoadImages(keyFrameUrls.value);
    const { width, height } = frames.value[0];
    canvas.width = width;
    canvas.height = height;
    const { imgNode } = await addImageToStage(
      stage.value as Konva.Stage,
      layer.value as Konva.Layer,
      canvas,
      {
        draggable: options.draggable,
        id: options.nodeProps?.id,
        mateTraitAnimationId: options.nodeProps?.mateTraitAnimationId,
        transform: options.nodeProps?.transform,
      }
    );
    imgNode.attrs.url = keyFrameUrls.value[0];
    return imgNode;
  };

  const performNextFrame = (index: number) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(frames.value[index], 0, 0, canvas.width, canvas.height);
    layer.value!.draw();
  };

  const performAnimation = () => {
    if (interval) {
      clearTimeout(interval);
    }
    let index = 0;
    if (options.durationGetter) {
      const randomPlay = () => {
        interval = window.setTimeout(() => {
          performNextFrame(index);
          index = (index + 1) % frames.value.length;
          randomPlay();
        }, options.durationGetter!(index % frames.value.length));
      };
      randomPlay();
    } else {
      interval = window.setInterval(() => {
        performNextFrame(index);
        index = (index + 1) % frames.value.length;
      }, options.duration);
    }
  };

  const play = async () => {
    if (keyFrameUrls.value.length === 0) return;
    animationShape.value = await prepareFrames();
    performAnimation();
  };

  const stop = () => {
    if (interval) {
      clearInterval(interval);
    }
    performNextFrame(0);
  };

  return {
    frames,
    animationShape,
    play,
    stop,
  };
};

export const blinkDurationGetter = (index: number): number => {
  return index === 0 ? 200 : Math.random() * 300 + 3500;
};

export const durationGetter = (index: number, type: TraitAnimationType) => {
  if (type === TraitAnimationType.Blink) {
    return blinkDurationGetter(index);
  } else {
    return 1000;
  }
};
